import { useQuery } from '@apollo/client';
import {
  ALL_MODULES,
  AsyncPropSelector,
  T1,
  getTranslatedText,
  TEXT_LENGTH
} from '@nile/components';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import {
  Popover,
  TextField,
  Button,
  ButtonGroup
} from '@superops/ui-design-system';
import { Edit } from '@styled-icons/material/Edit';
import { cloneDeep, isEqual } from 'lodash';
import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { EditIcon } from '@nile/components/src/Icons';
import { useIntl } from 'react-intl';

const CustomField = styled.div`
  min-width: 200px;
  max-width: ${(props) => (props?.fullWidth ? 'unset' : '250px')};
  flex-grow: 1;
  padding-right: ${(props) => !props?.fullWidth && '10px'};
  padding-left: ${(props) => !props?.fullWidth && '10px'};
  margin-top: 8px;
  margin-bottom: 8px;
  .Polaris-ButtonGroup {
    margin-top: 12px;
  }
`;

const Label = styled.div`
  text-align: left;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 10px;
`;

const Wrap = styled.div`
  display: flex;
  align-items: center;
  .edit-actions {
    visibility: hidden;
    cursor: pointer;
  }
  padding-left: 8px;
  padding-right: 8px;
  max-width: 100%;
`;

const CField = styled.div`
  .editable-cell,
  .cell {
    flex-grow: 1;
    font-weight: bold;
  }
  &:hover {
    .edit-actions {
      visibility: visible;
    }
  }
`;

function AsyncText(props) {
  const {
    field,
    ticket,
    defaultValue,
    updateSelection,
    editable,
    value,
    inlineEdit,
    disableSelection,
    isMandatory,
    error
  } = props;

  const intl = useIntl();

  const [updatedValue, setUpdatedValue] = useState(value || '');
  useEffect(() => {
    if (!isEqual(updatedValue, value)) {
      setUpdatedValue(value);
    }
  }, [value]);
  const updateSelectedField = (val) => {
    const type = field.fieldType || field.uiDataType;
    const parsedVal = val || null;
    // switch (type) {
    //   case 'NUMERIC':
    //   case 'numeric':
    //   case 'DECIMAL':
    //   case 'decimal':
    //     parsedVal = Number(val);
    //     break;
    //   default:
    //     break;
    // }
    updateSelection(parsedVal);
  };
  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = useCallback(() => {
    setPopoverActive(!popoverActive);
  });
  const updateUpdatedValue = (val) => {
    setUpdatedValue(val || null);
  };
  const getField = () => {
    const type = field.fieldType || field.uiDataType;
    switch (type) {
      case 'TEXT':
      case 'text':
        return (
          <TextField
            onChange={inlineEdit ? updateUpdatedValue : updateSelectedField}
            value={inlineEdit ? updatedValue : value}
            placeholder={getTranslatedText(
              intl,
              'common.placeholder.enterValue'
            )}
            disabled={disableSelection}
            error={error}
            maxLength={TEXT_LENGTH.size_5}
          />
        );
      case 'PARAGRAPH':
      case 'paragraph':
        return (
          <TextField
            onChange={inlineEdit ? updateUpdatedValue : updateSelectedField}
            value={inlineEdit ? updatedValue : value}
            placeholder={getTranslatedText(
              intl,
              'common.placeholder.enterValue'
            )}
            multiline={4}
            disabled={disableSelection}
            error={error}
          />
        );
      case 'NUMERIC':
      case 'numeric':
        return (
          <TextField
            onChange={inlineEdit ? updateUpdatedValue : updateSelectedField}
            value={inlineEdit ? updatedValue : value}
            placeholder={getTranslatedText(
              intl,
              'common.placeholder.enterValue'
            )}
            maxLength={10}
            type="number"
            disabled={disableSelection}
            error={error}
          />
        );
      case 'DECIMAL':
      case 'decimal':
        return (
          <TextField
            onChange={inlineEdit ? updateUpdatedValue : updateSelectedField}
            value={inlineEdit ? updatedValue : value}
            placeholder={getTranslatedText(
              intl,
              'common.placeholder.enterValue'
            )}
            inputMode="decimal"
            type="number"
            disabled={disableSelection}
            error={error}
          />
        );
      default:
    }
  };
  const activator = <EditIcon onClick={() => togglePopoverActive()} />;
  const getTag = () => {
    return (
      <CustomField className="cf-wrapper">
        <Label className="cf-label">
          {field.label || ''}
          {isMandatory && (
            <>
              <span className="mandatory-star">*</span>
            </>
          )}
        </Label>
        {getField()}
        <div className="mt10">
          <ButtonGroup>
            <Button
              onClick={() => {
                togglePopoverActive();
                setUpdatedValue(value || '');
              }}
            >
              Cancel
            </Button>
            <Button
              primary
              onClick={() => {
                updateSelectedField(updatedValue);
                togglePopoverActive();
              }}
            >
              Save
            </Button>
          </ButtonGroup>
        </div>
      </CustomField>
    );
  };
  const displayVal = value;
  // if (loading) return <div>loading...</div>;
  if (inlineEdit) {
    return !popoverActive ? (
      <CField className="w100">
        <Wrap className={`${editable ? 'editable-cell' : 'cell'}`}>
          <div className="ellipsis" title={displayVal}>
            {displayVal || <DisabledTag>NA</DisabledTag>}
          </div>
          {editable && <div className="edit-actions">{activator}</div>}
        </Wrap>
      </CField>
    ) : (
      <CField className="w100">
        <Wrap className="editable-cell">
          <div className="ellipsis" title={displayVal}>
            {displayVal || <DisabledTag>NA</DisabledTag>}
          </div>
          <Popover
            activator={activator}
            active={popoverActive}
            onClose={() => {
              // togglePopoverActive()
            }}
          >
            {getTag()}
          </Popover>
        </Wrap>
      </CField>
    );
  }
  return (
    <CustomField fullWidth={props?.fullWidth} className="cf-wrapper">
      <Label className="cf-label">
        <T1>
          {field.label || ''}
          {isMandatory && (
            <>
              <span className="mandatory-star">*</span>
            </>
          )}
        </T1>
      </Label>
      <div className="flex col" key={field.name}>
        {getField()}
      </div>
    </CustomField>
  );
}

export default AsyncText;
